import { Trans, useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "src/store";

import { updateGroupParent } from "@dashboard/devices/store";
import { Group } from "@dashboard/devices/types";
import { ConfirmDialog } from "@devices/components/shared";
import { Typography } from "@mui/material";
import { showSuccessToast } from "@shared/toasts/Toasts";
import { getErrorMessage } from "@shared/toasts/useQueryErrorToast";

import { selectGroups } from "../store/selectors";

type Props = {
    group: Group;
    newParentId: number | null;
    onClose: () => void;
};

const getDescriptionKey = (group: Group, oldParent: Group | undefined, newParentId: number | null) => {
    if (group.parentId === null) {
        return "moveGroup.description.fromTopLevel";
    }
    if (!oldParent) {
        return newParentId === null
            ? "moveGroup.description.fromHiddenParentToTopLevel"
            : "moveGroup.description.fromHiddenParent";
    }
    return newParentId === null ? "moveGroup.description.toTopLevel" : "moveGroup.description.betweenGroups";
};

export const MoveGroupDialog = ({ group, newParentId, onClose }: Props) => {
    const { t } = useTranslation(["modals", "common"]);
    const dispatch = useDispatch();
    const groups = useSelector(selectGroups);

    const oldParent = groups.find((item) => item.id === group.parentId);
    const newParent = groups.find((item) => item.id === newParentId);
    const values = { name: group.name, oldParent: oldParent?.name, newParent: newParent?.name };

    const onConfirm = async () => {
        try {
            await dispatch(updateGroupParent({ id: group.id, parentId: newParentId })).unwrap();
        } catch (error) {
            throw new Error(getErrorMessage(error, t("modals:editGroup.toast.groupUpdatedError")));
        }
        showSuccessToast(t("modals:editGroup.toast.groupUpdatedSuccess"));
    };

    return (
        <ConfirmDialog
            open
            title={t("modals:moveGroup.title")}
            primaryActionText={t("modals:moveGroup.button.move")}
            secondaryActionText={t("common:button.cancel")}
            onConfirm={onConfirm}
            onClose={onClose}
        >
            <Typography variant="body1" gutterBottom>
                <Trans
                    ns="modals"
                    i18nKey={getDescriptionKey(group, oldParent, newParentId)}
                    values={values}
                    components={{ strong: <strong /> }}
                />
            </Typography>
            {newParentId !== null ? (
                <Typography variant="body1" gutterBottom>
                    <Trans
                        ns="modals"
                        i18nKey="moveGroup.accessGained"
                        count={group.statistics?.deviceCount ?? 0}
                        values={values}
                        components={{ strong: <strong /> }}
                    />
                </Typography>
            ) : null}
            {group.parentId !== null ? (
                <Typography variant="body1">
                    <Trans
                        ns="modals"
                        i18nKey={oldParent ? "moveGroup.accessLost" : "moveGroup.accessLostFromHiddenParent"}
                        values={values}
                        components={{ strong: <strong /> }}
                    />
                </Typography>
            ) : null}
        </ConfirmDialog>
    );
};
